import { IPaperSize, IScreenCanvasSize } from "../models/screen-canvas-sizes";

/**
 * How wide and how tall a report page is, and the frame allowances every surface that draws
 * around it must agree on.
 *
 * A report is sized by its PAPER, not by a viewport. `IScreenCanvasSize` carries both: the
 * `reportPaper` a user picked in the paper-size dropdown, and the plain `width`/`height` pair
 * that the UI-design surfaces (device menu, width drag handle) write for ordinary screens. The
 * two can disagree — the device menu writes a phone viewport onto whatever screen is open, and
 * on a mobile project that lands on a report the moment the user steps back into UI Design with
 * the report still loaded. When it does, every consumer that read `width` first drew a
 * phone-wide page under a dropdown that said "A4", while the rulers (which read the paper first)
 * drew the real one. Hence one derivation, used by all of them:
 *
 *   reportPaper -> the screen's own width/height -> A4
 *
 * `width`/`height` stay in the model deliberately: the code generators fall back to them when a
 * screen has no `reportPaper` at all (`ReportUIGenerator.GetReportPaperDimensions`,
 * `ClientUIGenerationBase.ResolveReportCanvasDimensions`), so a legacy entry that only carries
 * the pair still prints at its own size instead of silently becoming A4.
 *
 * Pure and dependency-free so the designer, the canvas agent and the tests can all share it.
 *
 * `reportOrientation` on the same record has no reader or writer anywhere today. If landscape is
 * ever implemented by swapping the stored pair while keeping a portrait paper, this derivation
 * and `isReportCanvasEntryStale` must learn about it first — otherwise the repair would save the
 * portrait pair back over the user's landscape page.
 */

/** A4 at 96 DPI — what a report falls back to when nothing else says otherwise. */
export const DEFAULT_REPORT_PAGE_WIDTH_PX: number = 794;

export const DEFAULT_REPORT_PAGE_HEIGHT_PX: number = 1123;

/**
 * The horizontal padding `.kuika_designArea_reportDesigner` puts around the page (36px each
 * side). `box-sizing: border-box` is global, so the design area must be this much wider than
 * the paper for its content box — which `.kuika_iframeWrapper` fills at `width: 100%` — to end
 * up exactly one page wide.
 */
export const REPORT_DESIGN_AREA_HORIZONTAL_PADDING_PX: number = 72;

/** The sliver of ruler that sits past the page's right edge, so the last tick is not clipped. */
export const REPORT_RULER_OVERHANG_PX: number = 12;

/** Page size in CSS pixels. */
export interface IReportCanvasSize {
  width: number;
  height: number;
}

const isUsableDimension = (value: number | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

const paperSize = (paper: IPaperSize | undefined): IReportCanvasSize | undefined => {
  if (!paper || !isUsableDimension(paper.width) || !isUsableDimension(paper.height)) {
    return undefined;
  }
  return { width: paper.width, height: paper.height };
};

const entrySize = (entry: IScreenCanvasSize | undefined): IReportCanvasSize | undefined => {
  if (!entry || !isUsableDimension(entry.width) || !isUsableDimension(entry.height)) {
    return undefined;
  }
  return { width: entry.width, height: entry.height };
};

/**
 * The page size every report surface must draw: the chosen paper, else the screen's own
 * width/height, else A4. A partial or non-finite value (a cleared custom-size input parses to
 * `NaN`) counts as absent rather than being passed on to a style string.
 */
export const getReportCanvasSize = (entry: IScreenCanvasSize | undefined): IReportCanvasSize => {
  const fromPaper: IReportCanvasSize | undefined = paperSize(entry?.reportPaper);
  if (fromPaper) {
    return fromPaper;
  }
  const fromEntry: IReportCanvasSize | undefined = entrySize(entry);
  if (fromEntry) {
    return fromEntry;
  }
  return { width: DEFAULT_REPORT_PAGE_WIDTH_PX, height: DEFAULT_REPORT_PAGE_HEIGHT_PX };
};

/** The same derivation for a paper on its own, before it has been stored on a screen. */
export const getPaperPageSize = (paper: IPaperSize | undefined): IReportCanvasSize => {
  return paperSize(paper) ?? { width: DEFAULT_REPORT_PAGE_WIDTH_PX, height: DEFAULT_REPORT_PAGE_HEIGHT_PX };
};

/**
 * Whether the record says anything about the page's size at all. What separates a report that
 * was given a page from one that should simply fill the canvas — the two are drawn differently.
 */
export const hasExplicitReportPage = (entry: IScreenCanvasSize | undefined): boolean => {
  return paperSize(entry?.reportPaper) !== undefined || entrySize(entry) !== undefined;
};

/** The design area is one page wide plus its own padding. */
export const getReportDesignAreaWidth = (entry: IScreenCanvasSize | undefined): number =>
  getReportCanvasSize(entry).width + REPORT_DESIGN_AREA_HORIZONTAL_PADDING_PX;

/** The ruler spans the page plus a small overhang. */
export const getReportRulerWidth = (entry: IScreenCanvasSize | undefined): number =>
  getReportCanvasSize(entry).width + REPORT_RULER_OVERHANG_PX;

/**
 * Whether the entry's own `width`/`height` contradict its paper — i.e. something that is not the
 * paper-size dropdown wrote them. The report designer repairs such an entry once on open so the
 * generators' fallback, and any older client reading the pair, agree with what is on screen.
 */
export const isReportCanvasEntryStale = (entry: IScreenCanvasSize | undefined): boolean => {
  const fromPaper: IReportCanvasSize | undefined = paperSize(entry?.reportPaper);
  if (!entry || !fromPaper) {
    return false;
  }
  return entry.width !== fromPaper.width || entry.height !== fromPaper.height;
};
